import { assoc, compose, filter, is, map, mergeAll, pathOr, pick, prop, propOr, omit } from 'ramda'
import { assocOrDissocValue } from '../../../../utils/helpers/requestCleaner'
import { formatApiDate } from '../../../../utils/helpers/date'
import { formatAddressForApi } from '../../../../utils/helpers/address'
import { getPlaceLevelId } from '../../../../utils/helpers/place'
import { cleanDynamicValueV2 } from '../../../../utils/helpers/dynamicInputs'
import { hasValue } from '../../../../utils/helpers/value'

const cleanConditionValue = value => {
  if (is(Array, value)) return value.map(cleanConditionValue)
  return value?.reference || value?.id || value
}

const REPLY_VALUE_CONDITIONS = ['_event_service_order_reply_value', '_form_reply_reply_value']

const MULTIPLE_REPLY_OPERATORS = ['any', 'nany', 'exactly']

const isMultipleReplyCondition = condition => {
  if (!REPLY_VALUE_CONDITIONS.includes(condition?.field)) return false
  const questionType = condition?.data?.question?.typeName
  if (questionType === 'multiple') return true
  if (questionType === 'select') return false
  if (MULTIPLE_REPLY_OPERATORS.includes(condition?.operator)) return true
  if (typeof condition?.origin_value === 'string' && condition.origin_value.includes('to_refs')) return true
  return false
}

const ensureMultipleReplyValue = condition => {
  if (!isMultipleReplyCondition(condition)) return condition
  const value = condition.value
  if (is(Array, value)) return condition
  if (value === undefined || value === null || value === '') return assoc('value', [], condition)
  return assoc('value', [value], condition)
}

const cleanReplyValueValueCondition = condition => {
  if (!REPLY_VALUE_CONDITIONS.includes(condition.field)) return condition
  const data = condition.data || {}
  const question = data.question || {}
  const questionType = question.typeName
  const isMultiple = isMultipleReplyCondition(condition)
  const questionId = question?.id || data.question_id

  let valueKey = '{{raw value}}'
  if (isMultiple) valueKey = '{{to_refs value}}'
  else if (questionType === 'select') valueKey = '{{value.reference}}'

  let repliesKey = 'service_order.replies'
  if (condition.field === '_form_reply_reply_value') repliesKey = 'form_reply.replies'

  return compose(
    assoc(
      'origin_value',
      `::{{#find ${repliesKey} field="reply.question_id" operator="eq" value=${questionId}}}${valueKey}{{/find}}`
    ), //
    assoc(
      'data',
      compose(
        pick(['question_id']), //
        assoc('question_id', questionId)
      )(data)
    ) //
  )(condition)
}

const defaultOperator = condition => {
  if (condition.operator) return condition
  if (isMultipleReplyCondition(condition)) return assoc('operator', 'any', condition)
  return assoc('operator', 'eq', condition)
}

const cleanConditions = compose(
  JSON.stringify, //
  map(
    compose(
      pick(['value', 'operator', 'field', 'origin_value', 'data']),
      cleanReplyValueValueCondition,
      ensureMultipleReplyValue,
      condition => assoc('value', cleanConditionValue(condition.value), condition),
      defaultOperator
    )
  ),
  propOr([], 'conditions')
)

const cleanHeaders = compose(
  mergeAll,
  map(item => ({ [item.key]: item.value })),
  filter(item => !!item.value && !!item.key)
)

const cleanFieldValuesRequest = compose(
  map(
    compose(
      pick(['field_id', 'value', 'id']), //
      item => assoc('value', cleanDynamicValueV2(item.value, item.field.type), item)
    )
  ),
  filter(compose(hasValue, prop('value')))
)

const cleanEmptyValue = value => {
  if (!value) return null

  return value
}

const cleanServiceOrderUserOrGroup = (payload, trigger) => {
  if (trigger.action === 'insert_service_orders' || trigger.action === 'schedule_insert_service_orders') {
    return payload.map(so_item => {
      if (Array.isArray(so_item?.user_or_group)) {
        return compose(
          item =>
            assoc(
              'user_or_group',
              item.user_or_group.map(group => ({
                id: group.id,
                name: group.name,
                __typename: group.__typename
              })),
              item
            ),
          item =>
            assoc(
              'user_group_ids',
              item.user_or_group.map(group => group.id),
              item
            ),
          item => assoc('user_id', null, item),
          item => assoc('group_id', null, item)
        )(so_item)
      }

      if (so_item?.user_or_group?.__typename === 'User') {
        return compose(
          item =>
            assoc(
              'user_or_group',
              {
                id: cleanEmptyValue(item.user_or_group.contract?.id || item.user_or_group.id),
                name: item.user_or_group.name,
                __typename: item.user_or_group.__typename,
                user_id: cleanEmptyValue(item.user_or_group.user_id || item.user_or_group.id)
              },
              item
            ),
          item => assoc('user_id', cleanEmptyValue(item.user_or_group.user_id || item.user_or_group.id), item),
          item => assoc('group_id', null, item)
        )(so_item)
      }

      if (so_item?.user_or_group?.__typename === 'UserGroup') {
        return compose(
          item =>
            assoc(
              'user_or_group',
              {
                id: item.user_or_group.contract?.id || item.user_or_group.id,
                name: item.user_or_group.name,
                __typename: item.user_or_group.__typename,
                group_id: item.user_or_group.user_id || item.user_or_group.id
              },
              item
            ),
          item => assoc('user_id', null, item),
          item => assoc('group_id', item.user_or_group.user_id || item.user_or_group.id, item)
        )(so_item)
      }

      return compose(
        item => assoc('user_or_group', null, item),
        item => assoc('user_id', null, item),
        item => assoc('group_id', null, item)
      )(so_item)
    })
  }
  if (trigger.action === 'insert_service_order' || trigger.action === 'schedule_insert_service_order') {
    if (payload?.user_or_group?.__typename === 'User') {
      return compose(
        item =>
          assoc(
            'user_or_group',
            {
              id: cleanEmptyValue(item.user_or_group.contract?.id || item.user_or_group.id),
              name: item.user_or_group.name,
              __typename: item.user_or_group.__typename,
              user_id: cleanEmptyValue(item.user_or_group.user_id || item.user_or_group.id)
            },
            item
          ),
        item => assoc('user_id', cleanEmptyValue(item.user_or_group.user_id || item.user_or_group.id), item),
        item => assoc('group_id', null, item)
      )(payload)
    }

    if (payload?.user_or_group?.__typename === 'UserGroup') {
      return compose(
        item =>
          assoc(
            'user_or_group',
            {
              id: item.user_or_group.contract?.id || item.user_or_group.id,
              name: item.user_or_group.name,
              __typename: item.user_or_group.__typename,
              group_id: item.user_or_group.user_id || item.user_or_group.id
            },
            item
          ),
        item => assoc('user_id', null, item),
        item => assoc('group_id', item.user_or_group.user_id || item.user_or_group.id, item)
      )(payload)
    }

    if (Array.isArray(payload?.user_or_group)) {
      return compose(
        item => assoc('user_id', null, item),
        item => assoc('group_id', null, item),
        item =>
          assoc(
            'user_group_ids',
            item.user_or_group.map(group => group.id),
            item
          )
      )(payload)
    }

    return compose(
      item => assoc('user_or_group', null, item),
      item => assoc('user_id', null, item),
      item => assoc('group_id', null, item),
      item => assoc('user_group_ids', null, item)
    )(payload)
  }

  if (trigger.action !== 'update_service_order_user_or_group') return payload

  if (payload.content?.__typename === 'User') {
    return compose(item =>
      assoc(
        'content',
        { id: item.content.id, name: item.content.name, __typename: item.content.__typename, user_id: item.content.id },
        item
      )
    )(payload)
  }

  if (
    payload.content?.__typename === 'Group' ||
    payload.content?.__typename === 'UserGroup' ||
    Array.isArray(payload.content)
  ) {
    const transform = item =>
      assoc(
        'content',
        {
          id: item.content.id,
          name: item.content.name,
          __typename: item.content.__typename,
          group_id: item.content.id
        },
        item
      )

    if (Array.isArray(payload.content)) {
      return assoc(
        'content',
        payload.content.map(content => ({
          id: content.id,
          name: content.name,
          __typename: content.__typename,
          group_id: content.id
        })),
        payload
      )
    }

    return transform(payload)
  }
}

const cleanServiceOrderPayload = (payload, trigger) => {
  const supported = ['insert_service_order', 'schedule_insert_service_order']

  if (!supported.includes(trigger?.action)) return payload

  return compose(
    pick([
      'field_values',
      'user_id',
      'checklist_id',
      'place_id',
      'asset_id',
      'scheduling',
      'priority_id',
      'address',
      'description',
      'parent_id',
      'parent_dependent',
      'documents',
      'group_id',
      'user_group_ids',
      'type',
      'user_or_group',
      'non_compliance_id',
      'form_reply_id',
      'bond',
      'typeSelect',
      'metadata',
      'name'
    ]),
    item => assoc('scheduling', formatApiDate(item.scheduling), item),
    item => assoc('place_id', pathOr(null, ['place', 'id'], item), item),
    item => assoc('asset_id', pathOr(null, ['asset', 'id'], item), item),
    item => assoc('checklist_id', pathOr(null, ['checklist', 'id'], item), item),
    item => assoc('field_values', cleanSnakeCase(cleanFieldValuesRequest(payload.field_values || [])), item)
  )(payload)
}

const cleanAssetPayload = (payload, trigger) => {
  if (trigger.action !== 'update_asset') return payload
  return compose(pick(['place_id']), item => assoc('place_id', pathOr(null, ['place', 'id'], item), item))(payload)
}

const isValidAddress = item => {
  let value = typeof item !== 'string' ? JSON.stringify(item) : item
  try {
    value = JSON.parse(value)
  } catch (e) {
    return false
  }

  return typeof value === 'object' && value !== null
}

const cleanAddress = item => {
  if (isValidAddress(item.value))
    return compose(
      pick(['fieldId', 'value']), //
      it => assoc('value', JSON.parse(it.value), it)
    )(item)

  return item
}

const cleanSnakeCase = item => {
  return map(
    compose(
      pick(['field', 'field_id', 'value']), //
      it => assoc('field_id', it.field_id || it.fieldId, it),
      cleanAddress
    ),
    item
  )
}

const cleanServiceOrdersPayload = (payload, trigger) => {
  const supported = ['insert_service_orders', 'schedule_insert_service_orders']

  if (!supported.includes(trigger?.action)) return payload

  return payload.map(item => cleanServiceOrderPayload(item, { action: 'insert_service_order' }))
}

const cleanPlacePayload = (payload, trigger) => {
  if (trigger.action !== 'insert_place') return payload
  return compose(
    pick(['place_level_id', 'name', 'type']), //
    item => assoc('place_level_id', getPlaceLevelId(item), item)
  )(payload)
}

const cleanWebhookPayload = (payload, trigger) => {
  if (trigger.action !== 'webhook') return payload
  return compose(pick(['url', 'headers']), payload =>
    assocOrDissocValue('headers', payload?.headers ? cleanHeaders(payload.headers) : undefined, payload)
  )(payload)
}

const cleanEmailPayload = (payload, trigger) => {
  if (trigger.action !== 'email') return payload
  return compose(pick(['to', 'subject', 'content', 'attachments']))(payload)
}

const cleanSlackPayload = (payload, trigger) => {
  if (trigger.action !== 'slack_channel_message') return payload

  return compose(
    pick(['message', 'channel', 'attachments']) //
  )(payload)
}
const cleanSlackDmPayload = (payload, trigger) => {
  if (trigger.action !== 'slack_direct_message') return payload

  return compose(
    pick(['message', 'user', 'attachments']), //
    item =>
      assoc(
        'user',
        compose(
          pick(['email', 'field_origin_email']),
          assoc('email', item.user?.email.replace('#field_id#', payload?.user?.field_origin_email?.id)), //
          assoc('field_origin_email', item.user?.field_origin_email)
        )(item),
        item
      )
  )(payload)
}

const cleanD4signGenPDFAndSendToSignPayload = (payload, trigger) => {
  if (trigger.action !== 'd4sign_gen_pdf_and_send_to_sing') return payload

  return compose(
    pick(['safe', 'folder', 'signers', 'file_name', 'template_id']), //
    item => assoc('template_id', pathOr(null, ['template', 'id'], item), item)
  )(payload)
}

const cleanSignersD4Sign = ({ signers, workflow }) => {
  if (!workflow) return signers

  return signers?.map((it, index) => ({ ...(it ?? {}), after_position: index }))
}

const cleanD4signSendToSignPayload = (payload, trigger) => {
  if (trigger.action !== 'd4sign_send_to_sing') return payload

  return compose(
    pick(['safe', 'folder', 'signers', 'file_name', 'document', 'public_token', 'workflow']), //
    assoc('signers', cleanSignersD4Sign(payload)),
    assoc('workflow', propOr(false, 'workflow', payload))
  )(payload)
}

const cleanD4signAttachAtOriginPayload = (payload, trigger) => {
  if (trigger.action !== 'd4sign_attach_document') return payload

  return compose(
    pick(['origin']) //
  )(payload)
}

const cleanGoogleCalendarPayload = (payload, trigger) => {
  if (trigger.action !== 'google_calendar_insert_event') return payload

  return compose(
    pick(['name', 'description', 'reminders', 'duration', 'start']) //
  )(payload)
}

const cleanDisablePlacePayload = (payload, trigger) => {
  if (trigger.action !== 'place_lock') return payload
  return compose(pick(['reason', 'checklist_id']), item =>
    assoc('checklist_id', pathOr(null, ['checklist', 'id'], item), item)
  )(payload)
}

const cleanEnablePlacePayload = (payload, trigger) => {
  if (trigger.action !== 'place_unlock') return payload
  return compose(pick(['checklist_id']), item => assoc('checklist_id', pathOr(null, ['checklist', 'id'], item), item))(
    payload
  )
}

const cleanUpdatePlacePayload = (payload, trigger) => {
  if (!payload) return payload
  if (trigger.action !== 'update_place') {
    if (!payload?.name) {
      delete payload?.name
    }
    if (!payload?.field_values?.length) {
      delete payload?.field_values
    }
    return payload
  }
  const { name, field_values } = payload

  const fieldValuesWithValue =
    field_values?.filter(fv => {
      return !!fv.value
    }) || []

  const remapped = fieldValuesWithValue.map(fv => {
    return {
      name: fv.field?.name || fv.name,
      field_id: fv.field_id,
      value: fv.value
    }
  })

  return {
    ...payload,
    ...{ name, field_values: remapped }
  }
}

// Deriva `user_id`/`group_id`/`user_or_group` a partir do valor bruto de `UsersOrGroupsSelect`
// (mesmo componente/mesma forma de saída usado em `InsertServiceOrderSentence`), reaproveitando a
// mesma lógica de `cleanServiceOrderUserOrGroup` (branch `insert_service_order`) — mas, ao
// contrário daquele caso (que sempre reseta os três campos, pois `insert_service_order` sempre
// envia o payload inteiro), aqui o campo é opcional: se `user_or_group` não foi tocado, nenhum dos
// três é incluído no payload (ver chamada abaixo, que só roda quando `item.user_or_group` existe).
const buildUpdateChildUserOrGroup = item => {
  if (item.user_or_group?.__typename === 'User') {
    return compose(
      it =>
        assoc(
          'user_or_group',
          {
            id: cleanEmptyValue(it.user_or_group.contract?.id || it.user_or_group.id),
            name: it.user_or_group.name,
            __typename: it.user_or_group.__typename,
            user_id: cleanEmptyValue(it.user_or_group.user_id || it.user_or_group.id)
          },
          it
        ),
      it => assoc('user_id', cleanEmptyValue(it.user_or_group.user_id || it.user_or_group.id), it)
    )(item)
  }

  if (item.user_or_group?.__typename === 'UserGroup') {
    return compose(
      it =>
        assoc(
          'user_or_group',
          {
            id: it.user_or_group.contract?.id || it.user_or_group.id,
            name: it.user_or_group.name,
            __typename: it.user_or_group.__typename,
            group_id: it.user_or_group.user_id || it.user_or_group.id
          },
          it
        ),
      it => assoc('group_id', it.user_or_group.user_id || it.user_or_group.id, it)
    )(item)
  }

  return item
}

// `update_child_service_order` tem todos os campos opcionais: só entra no payload o que o
// usuário de fato preencheu (mesmo espírito de `cleanUpdatePlacePayload` acima).
// `checklists` reaproveita o seletor de `complete_service_orders` (que guarda objetos
// `{id, name}` para exibição), mas o backend espera apenas `{id}` por checklist.
// `asset_id`/`priority_id`/`user_or_group`/`address`/`metadata`/`name` reaproveitam os mesmos
// componentes e as mesmas derivações já usadas por `insert_service_order`
// (`cleanServiceOrderPayload`/`cleanServiceOrderUserOrGroup`) e pela tela real de Atividade
// (`serviceOrders/editV2/_requests/cleanRequest.js`, para `priority_id`/`address`). `field_values`
// ("campos adicionais") reaproveita a mesma limpeza (`cleanFieldValuesRequest`/`cleanSnakeCase`) já
// usada por `insert_service_order` — não há filtro por questionário escolhido em `checklists`
// porque campos adicionais são por `origin` ("service_orders"), não por `checklist_id` (ver
// `useFieldsFromApi`): o mesmo catálogo vale independentemente de quantos/quais questionários
// estiverem selecionados ali.
// `target` escolhe qual atividade é atualizada: "child" (padrão, filha vinculada) ou "current" (a própria atividade que
// disparou a automação); automações salvas sem o campo continuam como "child". Com "current", `checklists` não é enviado.
// `tag` (tag de atividade) guarda o objeto escolhido só para exibir o nome ao reabrir a automação; o backend
// usa apenas `tag_id` (como em `assign_tag`) e SUBSTITUI a tag da filha. `documents` segue o mesmo formato de
// `insert_service_order` (lista de arquivos enviados; o backend usa só `id`/`description`) e é ACRESCENTADO aos
// documentos que a filha já tem. `automatic` (responsável "Automático") só vai quando `true`.
// `place`/`asset`/`priority` (objetos escolhidos nos seletores) são guardados no payload só para a tela
// reabrir a automação já preenchida (a sentença mostra o nome e o seletor mostra o valor escolhido); sem eles,
// ao reabrir e salvar, `place_id`/`asset_id`/`priority_id` seriam recalculados como nulos e apagados. O backend
// só lê `place_id`/`asset_id`/`priority_id` e ignora as demais chaves. Guardamos só o necessário
// (o objeto de Local/Ativo carregado do seletor é grande).
const pickSelectedForDisplay = value => (value?.id ? pick(['id', 'name', 'type'], value) : undefined)

// `status` (opcional): guarda o objeto escolhido (`{id, name, label}`) para a tela reabrir a automação já
// preenchida; o backend lê só o `id` (aceita id puro ou objeto com `id`). Em branco = não altera o status.
const pickStatusForDisplay = value => (value?.id ? pick(['id', 'name', 'label'], value) : undefined)

const cleanUpdateChildServiceOrderPayload = (payload, trigger) => {
  if (trigger.action !== 'update_child_service_order') return payload

  const cleaned = compose(
    pick([
      'target',
      'description',
      'place_id',
      'place',
      'scheduling',
      'checklists',
      'asset_id',
      'asset',
      'priority_id',
      'priority',
      'user_id',
      'group_id',
      'user_or_group',
      'automatic',
      'address',
      'metadata',
      'name',
      'field_values',
      'tag',
      'tag_id',
      'documents',
      'status'
    ]),
    item => assoc('target', item.target === 'current' ? 'current' : 'child', item),
    item => assoc('status', pickStatusForDisplay(item.status), item),
    item => assoc('field_values', cleanSnakeCase(cleanFieldValuesRequest(item.field_values || [])), item),
    item => assoc('tag_id', pathOr(null, ['tag', 'id'], item), item),
    item => buildUpdateChildUserOrGroup(item),
    item => assoc('address', item.address?.search ? formatAddressForApi(item.address) : null, item),
    item =>
      assoc(
        'checklists',
        (item.checklists || []).map(checklist => ({ id: checklist.id })),
        item
      ),
    item => assoc('scheduling', formatApiDate(item.scheduling), item),
    item => assoc('place_id', pathOr(null, ['place', 'id'], item), item),
    item => assoc('asset_id', pathOr(null, ['asset', 'id'], item), item),
    item => assoc('priority_id', pathOr(null, ['priority', 'id'], item), item),
    item => assoc('place', pickSelectedForDisplay(item.place), item),
    item => assoc('asset', pickSelectedForDisplay(item.asset), item),
    item => assoc('priority', pickSelectedForDisplay(item.priority), item)
  )(payload || {})

  if (!cleaned.description) delete cleaned.description
  if (!cleaned.place_id) {
    delete cleaned.place_id
    delete cleaned.place
  }
  if (!cleaned.scheduling) delete cleaned.scheduling
  if (!cleaned.checklists?.length) delete cleaned.checklists
  if (!cleaned.asset_id) {
    delete cleaned.asset_id
    delete cleaned.asset
  }
  if (!cleaned.priority_id) {
    delete cleaned.priority_id
    delete cleaned.priority
  }
  if (!cleaned.user_id) delete cleaned.user_id
  if (!cleaned.group_id) delete cleaned.group_id
  if (!cleaned.user_or_group || (Array.isArray(cleaned.user_or_group) && !cleaned.user_or_group.length)) {
    delete cleaned.user_or_group
  }
  if (!cleaned.address) delete cleaned.address
  if (!cleaned.metadata) delete cleaned.metadata
  if (!cleaned.name) delete cleaned.name
  if (!cleaned.field_values?.length) delete cleaned.field_values
  if (!cleaned.tag_id) {
    delete cleaned.tag_id
    delete cleaned.tag
  }
  if (!cleaned.documents?.length) delete cleaned.documents
  if (!cleaned.status?.id) delete cleaned.status

  // "Atividade atual" não usa o seletor de atividades filhas: não envia `checklists` (o backend também ignora).
  if (cleaned.target === 'current') delete cleaned.checklists

  // Responsável "Automático" tem prioridade e substitui qualquer usuário/grupo escolhido antes (o backend também
  // ignora esses campos quando `automatic` é verdadeiro).
  if (cleaned.automatic === true) {
    delete cleaned.user_or_group
    delete cleaned.user_id
    delete cleaned.group_id
  } else {
    delete cleaned.automatic
  }

  return cleaned
}

const cleanCompleteServiceOrdersPayload = (payload, trigger) => {
  if (trigger.action !== 'complete_service_orders') return payload
  return compose(
    pick(['days', 'checklists', 'fireAutomations']),
    item => assoc('days', Number(item.days), item),
    item => assoc('checklists', item.checklists, item),
    item => assoc('fireAutomations', item.fireAutomations, item)
  )(payload)
}

const cleanCompleteNonCompliancesPayload = (payload, trigger) => {
  if (trigger.action !== 'complete_non_compliances') return payload
  return compose(pick(['days']), item => assoc('days', item.days, item))(payload)
}

// `assign_tag` guarda a tag escolhida como objeto em `payload.tag` (para exibir o nome na
// sentença), mas o backend (`TriggerDispatcher.Actions.AssignTag`) espera só `{tag_id}`.
// `remove_tag` não tem configuração: sempre remove a tag atual, então vai com payload vazio.
const cleanAssignTagPayload = (payload, trigger) => {
  if (trigger.action !== 'assign_tag') return payload
  return compose(pick(['tag_id']), item => assoc('tag_id', pathOr(null, ['tag', 'id'], item), item))(payload)
}

const cleanRemoveTagPayload = (payload, trigger) => {
  if (trigger.action !== 'remove_tag') return payload
  return {}
}

const cleanPayload = trigger =>
  compose(
    JSON.stringify, //
    payload => cleanWebhookPayload(payload, trigger),
    payload => cleanEmailPayload(payload, trigger),
    payload => cleanServiceOrderPayload(payload, trigger),
    payload => cleanAssetPayload(payload, trigger),
    payload => cleanServiceOrdersPayload(payload, trigger),
    payload => cleanPlacePayload(payload, trigger),
    payload => cleanD4signGenPDFAndSendToSignPayload(payload, trigger),
    payload => cleanD4signSendToSignPayload(payload, trigger),
    payload => cleanSlackPayload(payload, trigger),
    payload => cleanSlackDmPayload(payload, trigger),
    payload => cleanGoogleCalendarPayload(payload, trigger),
    payload => cleanD4signAttachAtOriginPayload(payload, trigger),
    payload => cleanDisablePlacePayload(payload, trigger),
    payload => cleanEnablePlacePayload(payload, trigger),
    payload => cleanUpdatePlacePayload(payload, trigger),
    payload => cleanServiceOrderUserOrGroup(payload, trigger),
    payload => cleanUpdateChildServiceOrderPayload(payload, trigger),
    payload => cleanCompleteServiceOrdersPayload(payload, trigger),
    payload => cleanCompleteNonCompliancesPayload(payload, trigger),
    payload => cleanAssignTagPayload(payload, trigger),
    payload => cleanRemoveTagPayload(payload, trigger),
    propOr({}, 'payload')
  )(trigger)

const cleanRelativeConfiguration = (configuration, trigger) => {
  if (trigger.type !== 'relative') return configuration

  const measure = propOr(1, 'value')
  const value = propOr(1, 'value')

  return compose(
    pick(['time', 'field', 'entity', 'value', 'measure', 'moment']), //
    item =>
      assoc(
        'time', //
        value(item) * measure(item.measure),
        item
      )
  )(configuration)
}

const cleanConfiguration = trigger =>
  compose(
    JSON.stringify,
    configuration => cleanRelativeConfiguration(configuration, trigger),
    propOr({}, 'configuration')
  )(trigger)

export const cleanRequest = item =>
  compose(
    pick([
      'action',
      'entity',
      'event',
      'type',
      'payload',
      'conditions',
      'name',
      'folderId',
      'id',
      'frequency',
      'integrationId',
      'configuration'
    ]),
    item => assoc('folderId', pathOr(null, ['folder', 'id'], item), item),
    assoc('conditions', cleanConditions(item)),
    assoc('payload', cleanPayload(item)),
    assoc('configuration', cleanConfiguration(item))
  )(item)
